import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import './VariantEditor.css';

function VariantEditor({ variants, onChange }) {
  const [configErrors, setConfigErrors] = useState({});

  const updateVariant = (index, field, value) => {
    const updated = variants.map((v, i) => {
      if (i !== index) return v;
      return { ...v, [field]: value };
    });
    onChange(updated);
  };

  const handleConfigChange = (index, value) => {
    const errors = { ...configErrors };
    try {
      if (value.trim()) {
        JSON.parse(value);
      }
      delete errors[index];
    } catch {
      errors[index] = 'Invalid JSON';
    }
    setConfigErrors(errors);
    updateVariant(index, 'config', value);
  };

  const addVariant = () => {
    const nextKey = `variant_${String.fromCharCode(97 + variants.length)}`;
    onChange([
      ...variants,
      { key: nextKey, name: '', weight: 50, is_control: false, config: '{}' },
    ]);
  };

  const removeVariant = (index) => {
    if (variants.length <= 2) return;
    const updated = variants.filter((_, i) => i !== index);
    const errors = { ...configErrors };
    delete errors[index];
    setConfigErrors(errors);
    onChange(updated);
  };

  const setControl = (index) => {
    const updated = variants.map((v, i) => ({
      ...v,
      is_control: i === index,
    }));
    onChange(updated);
  };

  const totalWeight = variants.reduce((sum, v) => sum + (Number(v.weight) || 0), 0);

  return (
    <div className="variant-editor">
      <div className="variant-editor-header">
        <h3>Variants</h3>
        <button type="button" className="exp-btn-secondary" onClick={addVariant}>
          <Plus size={14} />
          Add Variant
        </button>
      </div>

      <div className="variant-weight-bar">
        {variants.map((v, i) => (
          <div
            key={i}
            className="variant-weight-segment"
            style={{
              width: totalWeight > 0 ? `${((Number(v.weight) || 0) / totalWeight) * 100}%` : '0%',
              backgroundColor: VARIANT_COLORS[i % VARIANT_COLORS.length],
            }}
            title={`${v.key}: ${v.weight}%`}
          />
        ))}
      </div>
      <div className="variant-weight-total">
        Total weight: {totalWeight}%
        {totalWeight !== 100 && (
          <span className="variant-weight-warning"> (should be 100%)</span>
        )}
      </div>

      {variants.map((variant, index) => (
        <div key={index} className="variant-card">
          <div className="variant-card-header">
            <div
              className="variant-color-dot"
              style={{ backgroundColor: VARIANT_COLORS[index % VARIANT_COLORS.length] }}
            />
            <span className="variant-label">Variant {index + 1}</span>
            {variant.is_control && <span className="variant-control-badge">Control</span>}
            {variants.length > 2 && (
              <button
                type="button"
                className="variant-remove-btn"
                onClick={() => removeVariant(index)}
                title="Remove variant"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>

          <div className="variant-fields">
            <div className="exp-form-group variant-field-key">
              <label className="exp-form-label">Key</label>
              <input
                type="text"
                className="exp-form-input"
                value={variant.key}
                onChange={(e) => updateVariant(index, 'key', e.target.value)}
                placeholder="variant_key"
              />
            </div>
            <div className="exp-form-group variant-field-name">
              <label className="exp-form-label">Name</label>
              <input
                type="text"
                className="exp-form-input"
                value={variant.name}
                onChange={(e) => updateVariant(index, 'name', e.target.value)}
                placeholder="Variant name"
              />
            </div>
            <div className="exp-form-group variant-field-weight">
              <label className="exp-form-label">Weight (%)</label>
              <input
                type="number"
                className="exp-form-input"
                value={variant.weight}
                onChange={(e) => updateVariant(index, 'weight', parseInt(e.target.value) || 0)}
                min="0"
                max="100"
              />
            </div>
            <div className="exp-form-group variant-field-control">
              <label className="exp-form-label">Control</label>
              <button
                type="button"
                className={`variant-control-toggle ${variant.is_control ? 'active' : ''}`}
                onClick={() => setControl(index)}
              >
                {variant.is_control ? 'Yes' : 'No'}
              </button>
            </div>
          </div>

          <div className="exp-form-group">
            <label className="exp-form-label">Config (JSON)</label>
            <textarea
              className={`exp-form-input variant-config-textarea ${configErrors[index] ? 'has-error' : ''}`}
              value={variant.config}
              onChange={(e) => handleConfigChange(index, e.target.value)}
              placeholder='{}'
              rows={3}
            />
            {configErrors[index] && (
              <span className="variant-config-error">{configErrors[index]}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const VARIANT_COLORS = ['#8B6F4E', '#6B8E6B', '#B8860B', '#708090', '#CD853F'];

export default VariantEditor;
